// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import axios, { AxiosInstance } from "axios";
import * as rax from "retry-axios";

export class APIService {
  private static _instance: AxiosInstance;
  private static _idToken: string;
  // Shared in-flight refresh promise: ensures that when several requests
  // 401 concurrently, only one token refresh call is made and every
  // caller awaits/reuses the same result instead of triggering its own.
  private static _refreshPromise: Promise<{ idToken: string }> | null = null;

  constructor(idToken: string, callback: () => Promise<{ idToken: string }>) {
    APIService._instance = axios.create();
    rax.attach(APIService._instance);
    APIService._idToken = idToken;
    APIService.updateRequestInterceptor();

    (APIService._instance.defaults as unknown as rax.RaxConfig).raxConfig = {
      retry: 3,
      instance: APIService._instance,
      httpMethodsToRetry: ["GET", "HEAD", "OPTIONS", "DELETE", "POST", "PATCH"],
      statusCodesToRetry: [[401, 401]],
      retryDelay: 100,

      onRetryAttempt: async (err) => {
        const cfg = rax.getConfig(err);

        var res = await APIService.refreshTokenOnce(callback);

        APIService.updateTokens(res.idToken);

        APIService._instance.interceptors.request.clear();
        APIService.updateRequestInterceptor();
      },
    };
  }

  public static getInstance(): AxiosInstance {
    return APIService._instance;
  }

  private static refreshTokenOnce(
    callback: () => Promise<{ idToken: string }>,
  ): Promise<{ idToken: string }> {
    if (!APIService._refreshPromise) {
      APIService._refreshPromise = callback().finally(() => {
        APIService._refreshPromise = null;
      });
    }

    return APIService._refreshPromise;
  }

  private static updateTokens(idToken: string) {
    APIService._idToken = idToken;
  }

  private static updateRequestInterceptor() {
    APIService._instance.interceptors.request.use(
      (config) => {
        config.headers.set("Authorization", "Bearer " + APIService._idToken);
        if ((window as any).config?.DEV_MODE) {
          config.headers.set("x-jwt-assertion", APIService._idToken);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }
}
